import Logo from './Logo';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';

import React, { useState } from 'react';
import { useClient, TIER_CONFIG, DEFAULT_LOYALTY_REWARDS } from '../contexts/ClientContext';
import { SparklesIcon, XIcon, LogOutIcon, CameraIcon, GiftIcon, MessageCircleIcon, StarIcon, HistoryIcon, UserIcon, CalendarIcon, HeartIcon, MailIcon, PhoneIcon, SaveIcon, CheckCircleIcon } from './Icons';
import LoyaltyTracker from './LoyaltyTracker';
import { ALL_MEDIUMS } from '../data/mediums';
import { Medium, InteractionHistory } from '../types';
import { getFavoriteMediumIds, toggleFavoriteMedium } from './favorites';
import { getApiUrl, formatImageUrl, compressImage } from '../services/socket';

interface ClientProfileModalProps {
    onClose: () => void;
    initialTab?: 'profile' | 'history' | 'loyalty' | 'favorites' | 'messages';
    onChatStart?: (medium: Medium) => void;
    onSelectMedium?: (medium: Medium) => void;
}

const AVATAR_SEEDS = ["Felix", "Aneka", "Midnight", "Luna", "Sol", "Astro", "Mystic", "Zen"];

const formatSafeTime = (val: any): string => {
    if (!val) return '';
    if (typeof val === 'string') {
        const timeMatch = val.match(/^(\d{1,2}:\d{2})(?::\d{2})?$/);
        if (timeMatch) return timeMatch[1];
    }
    try {
        const d = new Date(val);
        if (isNaN(d.getTime())) return typeof val === 'string' ? val : '';
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
        return typeof val === 'string' ? val : '';
    }
};

const ClientProfileModal: React.FC<ClientProfileModalProps> = ({ onClose, initialTab = 'profile', onChatStart, onSelectMedium }) => {
    const { client, updateClientProfile, logoutClient } = useClient();
    const [activeTab, setActiveTab] = useState<'profile' | 'history' | 'loyalty' | 'favorites' | 'messages'>(initialTab);
    const [favoriteMediumIds, setFavoriteMediumIds] = useState<string[]>(getFavoriteMediumIds());
    const [selectedHistoryItem, setSelectedHistoryItem] = useState<InteractionHistory | null>(null);

    React.useEffect(() => {
        const handleFavs = () => {
            setFavoriteMediumIds(getFavoriteMediumIds());
        };
        window.addEventListener('astro_favorites_updated', handleFavs);
        return () => window.removeEventListener('astro_favorites_updated', handleFavs);
    }, []);
    
    // Edit States
    const [phone, setPhone] = useState(client?.phone || "");
    const [address, setAddress] = useState(client?.address || "");
    const [birthDate, setBirthDate] = useState(client?.birthDate || "");
    const [isSaving, setIsSaving] = useState(false);

    if (!client) return null;

    const points = client.loyaltyPoints || 0;
    const tier = client.loyaltyTier || 'Bronze';
    const tierInfo = TIER_CONFIG[tier];

    // Résolution de la récompense récemment débloquée (via propriété directe ou historique)
    const recentReward = client.recentUnlockedReward || (() => {
        if (client.unlockedRewards && client.unlockedRewards.length > 0) {
            const lastId = client.unlockedRewards[client.unlockedRewards.length - 1];
            const rew = DEFAULT_LOYALTY_REWARDS.find(r => r.id === lastId);
            if (rew) {
                return {
                    id: rew.id,
                    title: rew.title,
                    description: rew.description,
                    cost: rew.cost,
                    icon: rew.icon,
                    category: rew.category,
                    badge: rew.badge,
                    unlockedAt: "Récemment débloqué"
                };
            }
        }
        // Vérifier dans les transactions si un achat a été fait
        const lastRedeemTx = client.loyaltyTransactions?.find(t => t.type === 'reward_redeemed');
        if (lastRedeemTx) {
            return {
                id: 'custom_reward',
                title: lastRedeemTx.description.replace('Astres convertis : ', '').replace(' \\uD83C\\uDF81', '').replace('\\uD83C\\uDF81', ''),
                description: 'Avantage débloqué grâce à la conversion de vos Astres.',
                cost: Math.abs(lastRedeemTx.amount),
                icon: 'gift',
                unlockedAt: lastRedeemTx.date
            };
        }
        return null;
    })();

    const handleSave = () => {
        setIsSaving(true);
        // Simulation d'un appel API
        setTimeout(() => {
            updateClientProfile({
                phone: phone,
                address: address,
                birthDate: birthDate
            });
            setIsSaving(false);
        }, 800);
    };

    const handleAvatarChange = (seed: string) => {
        updateClientProfile({
            avatar: `https://api.dicebear.com/7.x/avataaars/svg?seed=${seed}`
        });
    };

    const handleReconsult = (item: { mediumName: string; mediumImage?: string; mediumId?: number | string }) => {
        const baseMedium = ALL_MEDIUMS.find(m => m.id === item.mediumId || (m.name || '').toLowerCase() === (item.mediumName || '').toLowerCase());
        const found: Medium = baseMedium ? {
            ...baseMedium,
            imageUrl: item.mediumImage || baseMedium.imageUrl
        } : {
            id: Number(item.mediumId) || 9999,
            name: item.mediumName,
            imageUrl: item.mediumImage || 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=300',
            rating: 4.9,
            reviews: 120,
            reviewsList: [],
            status: 'available',
            specialties: ['Tarot', 'Voyance'],
            description: 'Expert consulté précédemment.',
            experienceYears: 5,
            tags: ['En ligne']
        };
        onClose();
        if (onChatStart) {
            onChatStart(found);
        }
    };

    return (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-2 pb-20 sm:p-4 md:p-6 animate-fade-in">
            <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={onClose}></div>

            <div className="relative bg-[#0c061a] w-full max-w-4xl h-[92vh] max-h-[100dvh] rounded-3xl border border-purple-900/50 shadow-[0_0_60px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden">
                
                {/* BANDEAU SUPÉRIEUR IDENTIQUE AU MENU */}
                <div className="p-4 sm:p-5 border-b border-purple-900/40 flex items-center justify-between gap-3 shrink-0 bg-[#0c061a]/95">
                    <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-gold-500/20 via-purple-600/30 to-amber-500/20 border border-gold-500/40 flex items-center justify-center text-xl shadow-[0_0_15px_rgba(251,191,36,0.2)]">
                            
                        </div>
                        <div className="flex flex-col leading-tight">
                            <h1 className="text-xl sm:text-2xl font-serif font-black tracking-wider text-amber-300 drop-shadow-[0_2px_10px_rgba(251,191,36,0.5)]">
                                AstroX
                            </h1>
                            <span className="text-[10px] text-purple-200/90 uppercase tracking-widest font-semibold">Espace Client VIP</span>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <a 
                            href="tel:0985405274" 
                            className="hidden sm:flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-gradient-to-r from-emerald-950/90 to-green-900/90 text-white border border-emerald-500/50 shadow-md text-xs font-bold"
                        >
                            <PhoneIcon className="w-3 h-3 text-emerald-300" />
                            <span>09 85 40 52 74</span>
                        </a>

                        <button 
                            onClick={onClose} 
                            className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-bold transition-all shadow-sm active:scale-95 cursor-pointer border bg-purple-950/60 hover:bg-purple-900/80 text-gold-300 border-gold-500/40 hover:border-gold-400"
                        >
                            <span>Fermer</span>
                            <XIcon className="w-4 h-4 text-amber-300" />
                        </button>
                    </div>
                </div>

                {/* ZONE DÉROULANTE CENTRALE */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 flex flex-col gap-3 custom-scrollbar">
                    
                    {/* CARTE 1 : INFO CLIENT + MON COMPTE */}
                    <div className="flex justify-between items-center bg-gradient-to-r from-purple-950/70 via-black/90 to-purple-950/70 p-3.5 sm:p-4 rounded-2xl border border-gold-500/30 shadow-lg">
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <img 
                                    src={client.avatar} 
                                    alt="Avatar" 
                                    className="w-11 h-11 sm:w-12 sm:h-12 rounded-full border border-gold-400 object-cover shadow" 
                                />
                                <div className="absolute bottom-0 right-0 w-3.5 h-3.5 bg-emerald-500 rounded-full border-2 border-black"></div>
                            </div>
                            <div>
                                <span className="font-bold text-white block text-sm sm:text-base">{client.name}</span>
                                <span className="text-xs text-gold-400 font-semibold flex items-center gap-1">
                                    {points} pts • {tier}
                                </span>
                            </div>
                        </div>
                        <span className="text-xs bg-gold-500 text-black font-extrabold px-3 py-1.5 rounded-full shadow-md">
                            Mon Compte
                        </span>
                    </div>

                    {/* GRILLE 2 COLONNES EXACTEMENT COMME LE MENU */}
                    <div className="grid grid-cols-2 gap-2 sm:gap-2.5">
                        {/* Mon Profil */}
                        <button 
                            onClick={() => setActiveTab('profile')} 
                            className={`p-3 rounded-2xl border transition-all text-xs sm:text-sm font-bold flex items-center justify-between cursor-pointer shadow-md ${
                                activeTab === 'profile' 
                                    ? 'bg-gold-500/20 border-gold-400 text-gold-300 shadow-[0_0_15px_rgba(251,191,36,0.25)]' 
                                    : 'bg-purple-950/40 hover:bg-purple-900/60 border-purple-900/40 hover:border-gold-500/40 text-gray-200 hover:text-white'
                            }`}
                        >
                            <span>Mon Profil</span>
                            <UserIcon className="w-4 h-4 text-purple-300" />
                        </button>

                        {/* Points & Fidélité */}
                        <button 
                            onClick={() => setActiveTab('loyalty')} 
                            className={`p-3 rounded-2xl border transition-all text-xs sm:text-sm font-bold flex items-center justify-between cursor-pointer shadow-md ${
                                activeTab === 'loyalty' 
                                    ? 'bg-gold-500/20 border-gold-400 text-gold-300 shadow-[0_0_15px_rgba(251,191,36,0.25)]' 
                                    : 'bg-purple-950/40 hover:bg-purple-900/60 border-purple-900/40 hover:border-gold-500/40 text-gray-200 hover:text-white'
                            }`}
                        >
                            <span>Fidélité & Astres</span>
                            <span className="text-xs bg-gold-500/20 text-gold-300 border border-gold-500/30 px-2 py-0.5 rounded-full font-black">
                                {points} pts
                            </span>
                        </button>

                        {/* Historique */}
                        <button 
                            onClick={() => setActiveTab('history')} 
                            className={`p-3 rounded-2xl border transition-all text-xs sm:text-sm font-bold flex items-center justify-between cursor-pointer shadow-md ${
                                activeTab === 'history' 
                                    ? 'bg-gold-500/20 border-gold-400 text-gold-300 shadow-[0_0_15px_rgba(251,191,36,0.25)]' 
                                    : 'bg-purple-950/40 hover:bg-purple-900/60 border-purple-900/40 hover:border-gold-500/40 text-gray-200 hover:text-white'
                            }`}
                        >
                            <span>Historique</span>
                            <HistoryIcon className="w-4 h-4 text-purple-300" />
                        </button>

                        {/* Mes Favoris */}
                        <button 
                            onClick={() => setActiveTab('favorites')} 
                            className={`p-3 rounded-2xl border transition-all text-xs sm:text-sm font-bold flex items-center justify-between cursor-pointer shadow-md ${
                                activeTab === 'favorites' 
                                    ? 'bg-gold-500/20 border-gold-400 text-gold-300 shadow-[0_0_15px_rgba(251,191,36,0.25)]' 
                                    : 'bg-purple-950/40 hover:bg-purple-900/60 border-purple-900/40 hover:border-gold-500/40 text-gray-200 hover:text-white'
                            }`}
                        >
                            <span>Mes Favoris</span>
                            <span className="text-xs bg-pink-500/20 text-pink-300 border border-pink-500/30 px-2 py-0.5 rounded-full font-black">
                                {favoriteMediumIds.length}
                            </span>
                        </button>

                        {/* Messages Admin */}
                        <button 
                            onClick={() => setActiveTab('messages')} 
                            className={`p-3 rounded-2xl border transition-all text-xs sm:text-sm font-bold flex items-center justify-between cursor-pointer shadow-md ${
                                activeTab === 'messages' 
                                    ? 'bg-gold-500/20 border-gold-400 text-gold-300 shadow-[0_0_15px_rgba(251,191,36,0.25)]' 
                                    : 'bg-purple-950/40 hover:bg-purple-900/60 border-purple-900/40 hover:border-gold-500/40 text-gray-200 hover:text-white'
                            }`}
                        >
                            <span>Messages Admin</span>
                            {(client.adminMessages || []).filter(m => !m.read).length > 0 ? (
                                <span className="text-[10px] bg-red-600 text-white font-black px-1.5 py-0.5 rounded-full animate-pulse">
                                    {(client.adminMessages || []).filter(m => !m.read).length}
                                </span>
                            ) : (
                                <MessageCircleIcon className="w-4 h-4 text-purple-300" />
                            )}
                        </button>

                        {/* Se Déconnecter */}
                        <button 
                            onClick={() => { logoutClient(); onClose(); }} 
                            className="p-3 rounded-2xl border transition-all text-xs sm:text-sm font-bold flex items-center justify-between cursor-pointer shadow-md bg-red-950/40 hover:bg-red-900/60 border-red-500/40 hover:border-red-400 text-red-300 hover:text-white active:scale-95"
                        >
                            <span>Se Déconnecter</span>
                            <LogOutIcon className="w-4 h-4 text-rose-400" />
                        </button>
                    </div>

                    {/* CONTENU DE L'ONGLET SÉLECTIONNÉ */}
                    <div className="mt-2 bg-gradient-to-br from-mystic-950 to-black p-4 sm:p-6 rounded-2xl border border-white/5">
                        {/* Main Content */}
                <div className="flex-1 bg-gradient-to-br from-mystic-900 to-black p-6 md:p-10 overflow-y-auto custom-scrollbar">
                    
                    {/* BANDEAU SUPÉRIEUR : BADGE RÉCOMPENSE RÉCEMMENT DÉBLOQUÉE */}
                    {recentReward && (
                        <div className="mb-8 rounded-2xl bg-gradient-to-r from-emerald-950/90 via-slate-900 to-indigo-950/90 border-2 border-emerald-500/60 p-4 md:p-5 shadow-[0_0_35px_rgba(16,185,129,0.25)] relative overflow-hidden animate-fade-in group">
                            <div className="absolute top-0 right-0 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none -mr-10 -mt-10"></div>
                            
                            <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                <div className="flex items-start sm:items-center gap-3.5 min-w-0">
                                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br from-emerald-500/30 via-teal-500/20 to-indigo-600/40 border border-emerald-400/60 flex items-center justify-center shadow-lg shrink-0 group-hover:scale-105 transition-transform">
                                        <GiftIcon className="w-7 h-7 text-emerald-400" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap items-center gap-2 mb-1">
                                            <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full bg-emerald-400 text-black shadow-sm tracking-wide">
                                                <SparklesIcon className="w-3 h-3 text-black" />
                                                Récemment Débloqué
                                            </span>
                                            <span className="text-[10px] font-bold text-emerald-300 bg-emerald-950 border border-emerald-500/40 px-2 py-0.5 rounded-full font-mono">
                                                -{recentReward.cost} Astres Convertis
                                            </span>
                                            {recentReward.badge && (
                                                <span className="text-[10px] font-bold text-gold-300 bg-gold-950/80 border border-gold-500/40 px-2 py-0.5 rounded-full">
                                                    {recentReward.badge}
                                                </span>
                                            )}
                                        </div>
                                        <h4 className="text-base md:text-lg font-serif font-bold text-white truncate">
                                            {recentReward.title}
                                        </h4>
                                        <p className="text-xs text-gray-300 mt-0.5 line-clamp-2 md:line-clamp-none">
                                            {recentReward.description}
                                        </p>
                                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-2 text-[11px] text-gray-400">
                                            <span className="flex items-center gap-1"><CalendarIcon className="w-3 h-3 text-purple-400" /> {recentReward.unlockedAt}</span>
                                            <span>•</span>
                                            <span className="text-emerald-400 font-semibold flex items-center gap-1">
                                                <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-400" />
                                                Avantage Actif & Validé
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <button 
                                    onClick={() => setActiveTab('loyalty')}
                                    className="w-full sm:w-auto px-4 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider bg-gradient-to-r from-emerald-500 to-teal-400 hover:from-emerald-400 hover:to-teal-300 text-black shadow-lg shadow-emerald-950/50 hover:scale-105 transition-all whitespace-nowrap shrink-0 flex items-center justify-center gap-1.5"
                                >
                                    <GiftIcon className="w-4 h-4 text-black" />
                                    Voir dans Fidélité
                                </button>
                            </div>
                        </div>
                    )}
                    
                    {activeTab === 'profile' && (
                        <div className="animate-fade-in-up max-w-2xl">
                            <h3 className="text-2xl font-serif font-bold text-white mb-6 flex items-center gap-2">
                                Informations Personnelles
                            </h3>

                            <div className="space-y-6">
                                
                                {/* Avatar Selection */}
                                <div>
                                    <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-4">
                                        <label className="block text-xs font-bold text-gray-300 uppercase mb-1">Importer une photo personnalisée</label>
                                        <p className="text-[11px] text-gray-400 mb-3">Stockée directement sur le serveur VPS.</p>
                                        <label className="cursor-pointer bg-gradient-to-r from-gold-600 to-yellow-500 hover:from-gold-500 hover:to-yellow-400 text-black font-bold px-4 py-2 rounded-xl text-xs shadow-md inline-flex items-center gap-2 transition-transform hover:scale-105">
                                            <span>Téléverser une image</span>
                                            <input 
                                                type="file" 
                                                accept="image/*" 
                                                onChange={async (e) => {
                                                    const file = e.target.files?.[0];
                                                    if (!file || !client) return;
                                                    try {
                                                        const base64 = await compressImage(file, 800, 800, 0.85);
                                                        let avatarUrl = base64;
                                                        try {
                                                            const apiUrl = `${getApiUrl()}/api/upload`;
                                                            const res = await fetch(apiUrl, {
                                                                method: 'POST',
                                                                headers: { 'Content-Type': 'application/json' },
                                                                body: JSON.stringify({ image: base64, name: `avatar_${client.id}` })
                                                            });
                                                            if (res.ok) {
                                                                const data = await res.json();
                                                                if (data && data.url) {
                                                                    avatarUrl = formatImageUrl(data.url);
                                                                }
                                                            }
                                                        } catch (uploadErr) {
                                                            console.warn("API Upload VPS indisponible, utilisation du fallback Base64 avatar:", uploadErr);
                                                        }
                                                        updateClientProfile({ avatar: avatarUrl });
                                                    } catch (err) {
                                                        console.error("Erreur upload avatar VPS", err);
                                                    }
                                                }}
                                                className="hidden" 
                                            />
                                        </label>
                                    </div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase mb-3">Changer d'avatar</label>
                                    <div className="flex gap-3 overflow-x-auto pb-2">
                                        {AVATAR_SEEDS.map(seed => (
                                            <button 
                                                key={seed}
                                                onClick={() => handleAvatarChange(seed)}
                                                className="w-12 h-12 rounded-full border border-white/20 hover:scale-110 transition-transform bg-gray-800 overflow-hidden shrink-0"
                                            >
                                                <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${seed}`} alt={seed} />
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Personal Info */}
                                <div className="grid md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">ID Client (Non modifiable)</label>
                                        <input 
                                            type="text" 
                                            value={client.id} 
                                            disabled 
                                            className="w-full bg-black/30 border border-gray-800 rounded-lg px-4 py-3 text-gold-400 font-mono select-all cursor-not-allowed"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email (Non modifiable)</label>
                                        <input 
                                            type="text" 
                                            value={client.email} 
                                            disabled 
                                            className="w-full bg-black/30 border border-gray-800 rounded-lg px-4 py-3 text-gray-500 cursor-not-allowed"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gold-500 uppercase mb-1">Date de Naissance</label>
                                        <input 
                                            type="date" 
                                            value={birthDate} 
                                            onChange={(e) => setBirthDate(e.target.value)}
                                            className="w-full bg-black/50 border border-gray-600 rounded-lg px-4 py-3 text-white focus:border-gold-500 focus:outline-none color-scheme-dark font-medium"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gold-500 uppercase mb-1">Téléphone</label>
                                        <input 
                                            type="tel" 
                                            value={phone} 
                                            onChange={(e) => setPhone(e.target.value)}
                                            className="w-full bg-black/50 border border-gray-600 rounded-lg px-4 py-3 text-white focus:border-gold-500 focus:outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gold-500 uppercase mb-1">Ville / Adresse</label>
                                        <input 
                                            type="text" 
                                            value={address} 
                                            onChange={(e) => setAddress(e.target.value)}
                                            className="w-full bg-black/50 border border-gray-600 rounded-lg px-4 py-3 text-white focus:border-gold-500 focus:outline-none"
                                        />
                                    </div>
                                </div>

                                <div className="pt-4">
                                    <button 
                                        onClick={handleSave}
                                        disabled={isSaving}
                                        className="bg-gold-600 hover:bg-gold-500 text-black font-bold px-8 py-3 rounded-lg shadow-lg transition-transform hover:scale-105 flex items-center gap-2"
                                    >
                                        {isSaving ? 'Enregistrement...' : <><SaveIcon className="w-4 h-4" /> Sauvegarder les modifications</>}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'loyalty' && (
                        <div className="animate-fade-in-up">
                            <h3 className="text-2xl font-serif font-bold text-white mb-6 flex items-center gap-2">
                                Programme Fidélité Astral
                            </h3>
                            <LoyaltyTracker />
                        </div>
                    )}

                    {activeTab === 'history' && (
                        <div className="animate-fade-in-up">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-2xl font-serif font-bold text-white flex items-center gap-2">
                                    <span className="text-purple-400"></span> Historique des Consultations
                                </h3>
                                <div className="text-xs bg-white/5 border border-white/10 px-3 py-1.5 rounded-lg text-gray-300">
                                    Total : <strong className="text-gold-400">{client.history?.length || 0} consultations</strong>
                                </div>
                            </div>

                            <div className="space-y-4">
                                {(!client.history || client.history.length === 0) ? (
                                    <div className="text-center py-12 bg-white/5 rounded-xl border border-white/5">
                                        <MessageCircleIcon className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                                        <p className="text-gray-400">Aucune interaction enregistrée pour le moment.</p>
                                        <p className="text-xs text-gray-500 mt-2">Vos futures conversations apparaîtront ici et vous feront gagner des points de fidélité !</p>
                                    </div>
                                ) : (
                                    client.history.map((item, idx) => (
                                        <div key={item.id + idx} className="bg-gray-800/50 hover:bg-gray-800 transition-colors border border-gray-700 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                            <div className="flex items-center gap-4">
                                                <img 
                                                    src={item.mediumImage} 
                                                    alt={item.mediumName} 
                                                    onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=300'; }} 
                                                    className="w-14 h-14 rounded-full object-cover border border-purple-500/50 shrink-0"
                                                />
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <h4 className="text-white font-bold">{item.mediumName}</h4>
                                                        {item.pointsEarned && (
                                                            <span className="text-[10px] bg-gold-500/20 text-gold-300 font-bold px-2 py-0.5 rounded-full border border-gold-500/30">
                                                                +{item.pointsEarned} pts gagnés
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-gray-400 mt-0.5">{item.date} • Durée : {item.duration}</p>
                                                    {item.notes && <p className="text-sm text-gray-300 mt-1 italic">"{item.notes}"</p>}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleReconsult(item)}
                                                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-bold px-4 py-2.5 rounded-xl text-xs flex items-center justify-center gap-2 shadow-lg transition-transform hover:scale-105 shrink-0"
                                            >
                                                <MessageCircleIcon className="w-3.5 h-3.5" /> Reconsulter
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'favorites' && (
                        <div className="animate-fade-in-up">
                            <div className="flex items-center justify-between mb-6">
                                <h3 className="text-2xl font-serif font-bold text-white flex items-center gap-2">
                                    <HeartIcon className="w-3.5 h-3.5 text-pink-400 fill-pink-400 inline mr-1" /> Mes Médiums Favoris
                                </h3>
                                <div className="text-xs bg-white/5 border border-white/10 px-3 py-1.5 rounded-lg text-gray-300">
                                    Total : <strong className="text-pink-400">{favoriteMediumIds.length} favoris</strong>
                                </div>
                            </div>

                            {favoriteMediumIds.length === 0 ? (
                                <div className="text-center py-12 bg-white/5 rounded-xl border border-white/5">
                                    <HeartIcon className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                                    <p className="text-gray-400">Vous n'avez aucun médium dans vos favoris.</p>
                                    <p className="text-xs text-gray-500 mt-2">Cliquez sur le cœur sur la carte d'un expert pour l'ajouter ici pour un accès rapide !</p>
                                </div>
                            ) : (
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {favoriteMediumIds.map(id => {
                                        const medium = ALL_MEDIUMS.find(m => m.id === id);
                                        if (!medium) return null;
                                        return (
                                            <div key={medium.id} className="bg-mystic-800/60 border border-mystic-700 rounded-xl p-4 flex flex-col justify-between gap-4 relative group hover:border-pink-500/50 transition-all">
                                                <button 
                                                    onClick={() => toggleFavoriteMedium(medium.id)}
                                                    className="absolute top-3 right-3 p-1.5 bg-black/60 hover:bg-pink-600 text-pink-400 hover:text-white rounded-full transition-colors border border-white/10"
                                                    title="Retirer des favoris"
                                                >
                                                    <HeartIcon className="w-4 h-4 fill-current" />
                                                </button>
                                                
                                                <div className="flex items-center gap-3">
                                                    <img 
                                                        src={medium.imageUrl} 
                                                        alt={medium.name} 
                                                        onError={(e) => { (e.currentTarget as HTMLImageElement).src = 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=300'; }}
                                                        className="w-12 h-12 rounded-full object-cover border border-gold-500/40" 
                                                    />
                                                    <div className="min-w-0">
                                                        <h4 className="text-white font-bold truncate">{medium.name}</h4>
                                                        <p className="text-xs text-gold-400 truncate">{medium.role}</p>
                                                        <div className="flex items-center gap-1 text-[11px] text-gray-300 mt-0.5">
                                                            <StarIcon className="w-3 h-3 text-gold-400 fill-current" />
                                                            <span>{medium.rating}</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex gap-2 pt-2 border-t border-white/5">
                                                    <button 
                                                        onClick={() => { if (onSelectMedium) { onClose(); onSelectMedium(medium); }}}
                                                        className="flex-1 bg-white/10 hover:bg-white/20 text-white py-2 rounded-lg text-xs font-bold transition-colors"
                                                    >
                                                        Profil
                                                    </button>
                                                    <button 
                                                        onClick={() => { if (onChatStart) { onClose(); onChatStart(medium); }}}
                                                        className="flex-1 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 text-white py-2 rounded-lg text-xs font-bold transition-colors shadow-md flex items-center justify-center gap-1.5"
                                                    >
                                                        <MessageCircleIcon className="w-3.5 h-3.5" /> Consulter
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'messages' && (
                        <div className="animate-fade-in-up max-w-3xl">
                            <h3 className="text-2xl font-serif font-bold text-white mb-6 flex items-center gap-2">
                                <MailIcon className="w-3.5 h-3.5 text-cyan-400 inline mr-1" /> Messages de l'Administration
                            </h3>

                            {(!client.adminMessages || client.adminMessages.length === 0) ? (
                                <div className="text-center py-16 bg-white/5 border border-white/10 rounded-2xl p-8">
                                    <MessageCircleIcon className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                                    <h4 className="text-base font-bold text-white mb-1">Aucun message pour le moment</h4>
                                    <p className="text-gray-400 text-xs max-w-sm mx-auto">
                                        Les messages importants ou recommandations de vos conseillers et de l'administration apparaîtront ici et pourront être consultés à tout moment.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {client.adminMessages.map((msg) => (
                                        <div 
                                            key={msg.id} 
                                            className={`p-5 rounded-2xl border transition-all ${msg.read ? 'bg-mystic-800/40 border-white/10' : 'bg-cyan-950/30 border-cyan-500/40 shadow-[0_0_20px_rgba(6,182,212,0.15)]'}`}
                                        >
                                            <div className="flex items-start justify-between gap-3 mb-2">
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <h4 className="text-base font-bold text-white">{msg.title}</h4>
                                                        {!msg.read && (
                                                            <span className="text-[10px] bg-cyan-500 text-black px-2 py-0.5 rounded-full font-black uppercase tracking-wider">
                                                                Nouveau
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span className="text-[11px] text-gray-400">De : {msg.senderName || 'Administration AstroX'} • {msg.date}</span>
                                                </div>
                                                {!msg.read && (
                                                    <button
                                                        onClick={() => {
                                                            const updatedMessages = (client.adminMessages || []).map(m => m.id === msg.id ? { ...m, read: true } : m);
                                                            updateClientProfile({ adminMessages: updatedMessages });
                                                        }}
                                                        className="text-xs bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 px-3 py-1.5 rounded-xl font-bold transition-colors"
                                                    >
                                                        Marquer comme lu
                                                    </button>
                                                )}
                                            </div>
                                            <p className="text-gray-200 text-sm leading-relaxed whitespace-pre-wrap mt-3 bg-black/30 p-3.5 rounded-xl border border-white/5">
                                                {msg.message}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    </div>
                </div>
            </div>
            </div>

            {/* Modal de consultation d'une discussion passée */}
            {selectedHistoryItem && (
                <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in">
                    <div className="bg-mystic-900 border border-gold-500/40 rounded-2xl w-full max-w-2xl h-[80vh] flex flex-col shadow-2xl overflow-hidden">
                        <div className="bg-black/60 p-4 border-b border-white/10 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <img src={selectedHistoryItem.mediumImage} alt={selectedHistoryItem.mediumName} className="w-10 h-10 rounded-full object-cover border border-gold-500" />
                                <div>
                                    <h4 className="text-white font-bold font-serif">Discussion avec {selectedHistoryItem.mediumName}</h4>
                                    <p className="text-[11px] text-gray-400">{selectedHistoryItem.date} • Durée : {selectedHistoryItem.duration}</p>
                                </div>
                            </div>
                            <button onClick={() => setSelectedHistoryItem(null)} className="text-gray-400 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors">
                                <XIcon className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="flex-1 p-4 overflow-y-auto space-y-3 custom-scrollbar bg-gradient-to-b from-mystic-950 to-black">
                            {(!selectedHistoryItem.messages || selectedHistoryItem.messages.length === 0) ? (
                                <div className="text-center py-16 text-gray-400">
                                    <MessageCircleIcon className="w-12 h-12 mx-auto mb-3 text-gray-600" />
                                    <p>Aucun message archivé pour cette session.</p>
                                    <p className="text-xs text-gray-500 mt-1">Les messages de cette consultation s'afficheront ici.</p>
                                </div>
                            ) : (
                                selectedHistoryItem.messages.map((m, mIdx) => (
                                    <div key={m.id || mIdx} className={`flex flex-col ${m.sender === 'client' ? 'items-end' : 'items-start'}`}>
                                        <div className="flex items-center gap-1.5 mb-1">
                                            <span className="text-[10px] text-gray-400 font-bold">{m.sender === 'client' ? 'Vous' : selectedHistoryItem.mediumName}</span>
                                            <span className="text-[9px] text-gray-500">{formatSafeTime(m.timestamp)}</span>
                                        </div>
                                        <div className={`p-3 rounded-2xl max-w-[85%] text-sm ${m.sender === 'client' ? 'bg-indigo-600 text-white rounded-br-none' : 'bg-mystic-800 border border-gold-500/20 text-gray-200 rounded-bl-none'}`}>
                                            {m.text}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                        <div className="p-4 bg-black/60 border-t border-white/10 flex justify-end">
                            <button
                                onClick={() => {
                                    const item = selectedHistoryItem;
                                    setSelectedHistoryItem(null);
                                    handleReconsult(item);
                                }}
                                className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-bold px-5 py-2.5 rounded-xl text-xs flex items-center gap-2 hover:scale-105 transition-transform"
                            >
                                <MessageCircleIcon className="w-4 h-4" /> Reprendre la conversation avec {selectedHistoryItem.mediumName}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientProfileModal;
